<template>
    <div class="animated fadeIn">
        <b-card class="card-border mt-4">
            <b-card-title><i class="fa fa-flag"></i> Discrepancy Report</b-card-title>
            <b-card-sub-title>Summary of discrepancies in assets received from dispatches</b-card-sub-title>
            <div fluid class="px-2 mt-4">
                <loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

                <!-- Filter  -->
                <b-row class="my-2">
                    <b-col sm="12" md="3" lg="3">
                        <b-button v-b-popover.hover.right="'Toggle to show/hide filter options'" v-b-toggle.collapse-1
                            class="filter">
                            FILTER OPTIONS
                        </b-button>
                    </b-col>
                    <b-col sm="12">
                        <!-- Collapsible Filter Options -->
                        <b-collapse id="collapse-1" class="my-2" visible>
                            <b-card>
                                <!-- Date Range -->
                                <b-row no-gutters>
                                    <b-col lg="4" md="5" sm="12" class="mr-4">
                                        <b-form-group label="Date From">
                                            <b-form-datepicker name="Date From" v-model="filterBy.dateFrom" locale="en"
                                                reset-button label-reset-button="Clear"
                                                :date-format-options="dateFormatOptions"
                                                :date-disabled-fn="dateFromDisabled" v-validate="'required'" />
                                            <span v-show="errors.has('Date From')" class="help-block">
                                                {{ errors.first('Date From') }}
                                            </span>
                                        </b-form-group>
                                    </b-col>
                                    <b-col lg="4" md="5" sm="12" class="mr-4">
                                        <b-form-group label="Date To">
                                            <b-form-datepicker name="Date To" v-model="filterBy.dateTo" locale="en"
                                                reset-button label-reset-button="Clear"
                                                :date-format-options="dateFormatOptions"
                                                :date-disabled-fn="dateFromDisabled" v-validate="'required'" />
                                            <span v-show="errors.has('Date To')" class="help-block">
                                                {{ errors.first('Date To') }}
                                            </span>
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <!-- Source/Destination -->
                                <b-row no-gutters>
                                    <b-col lg="4" md="6" sm="12" class="mr-4">
                                        <b-form-group label="Source Company">
                                            <v-select class="style-chooser" label="text"
                                                placeholder=" - Please select - " :options="allSourceCompanyOptions"
                                                :reduce="(company) => company.value" v-model="filterBy.sourceCompany"
                                                v-validate="{ 'selectRequired': sourceIsRequired }">
                                                <template v-slot:no-options="{ search, searching }">
                                                    <template v-if="searching">
                                                        No results found for
                                                        <em>
                                                            <strong>{{ search }}</strong>
                                                        </em>
                                                    </template>
                                                    <em :style="{ opacity: 0.5 }" v-else>
                                                        Start typing to search for a company
                                                    </em>
                                                </template>
                                            </v-select>
                                        </b-form-group>
                                    </b-col>
                                    <b-col lg="4" md="6" sm="12" class="mr-4">
                                        <b-form-group label="Destination Company">
                                            <v-select class="style-chooser" label="text"
                                                placeholder=" - Please select - "
                                                :options="allDestinationCompanyOptions"
                                                :reduce="(company) => company.value"
                                                v-model="filterBy.destinationCompany">
                                                <template v-slot:no-options="{ search, searching }">
                                                    <template v-if="searching">
                                                        No results found for
                                                        <em>
                                                            <strong>{{ search }}</strong>
                                                        </em>
                                                    </template>
                                                    <em :style="{ opacity: 0.5 }" v-else>
                                                        Start typing to search for a company
                                                    </em>
                                                </template>
                                            </v-select>
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <b-row no-gutters>
                                    <b-col lg="4" md="6" sm="12" class="mr-4">
                                        <b-form-group label="Status">
                                            <b-form-select v-model="filterBy.status" :options="statusOptions"
                                                class="mr-2" />
                                        </b-form-group>
                                    </b-col>
                                    <b-col lg="4" md="6" sm="12" class="mr-4">
                                        <b-form-group label="Dispatch No."
                                            description="NOTE: Input the exact Dispatch No. to search">
                                            <b-form-input id="dispatchNo" name="Dispatch No" type="search"
                                                class="numFont" v-model="filterBy.dispatchNo"
                                                v-validate="getValidationParam(false, /^[A-Z0-9]{2}-[0-9]{6}-[0-9]{1,4}$/)"
                                                placeholder="XX-YYMMMDD-000" autocomplete="off" />
                                            <span v-show="errors.has('Dispatch No')" class="help-block">
                                                {{ errors.first('Dispatch No') }}
                                            </span>
                                        </b-form-group>
                                    </b-col>
                                </b-row>

                                <b-row no-gutters>
                                    <b-col sm="12">
                                        <b-button class="mr-1" variant="success" @click="onFilterRequest">
                                            Generate
                                        </b-button>
                                        <b-button class="mr-1" variant="primary" @click="onClickReset">
                                            Reset
                                        </b-button>
                                    </b-col>
                                </b-row>
                            </b-card>
                        </b-collapse>
                    </b-col>
                </b-row>

                <!-- Select Actions and Items Per Page Options -->
                <div v-if="items.length > 0">
                    <b-row>
                        <b-col sm="6" md="3" class="mt-4 mb-2">
                            <b-dropdown id="discrepancy-select-actions" text=" Select Actions " variant="dark"
                                slot="append">
                                <b-dropdown-item @click="bulkEditDiscrepancyReports" v-show="canBulkEdit">
                                    Bulk Discrepancy Report(s)
                                </b-dropdown-item>
                                <b-dropdown-item>
                                    <json-excel :data="exportData" :fields="exportFields" type="xls"
                                        :name="fileName + '.xls'">
                                        Export Discrepancy Report in Excel
                                    </json-excel>
                                </b-dropdown-item>
                                <b-dropdown-item>
                                    <json-excel :data="exportData" :fields="exportFields" type="csv"
                                        :name="fileName + '.csv'">
                                        Export Discrepancy Report to CSV
                                    </json-excel>
                                </b-dropdown-item>
                            </b-dropdown>
                        </b-col>

                        <b-col sm="6" md="4" offset-md="5" class="mt-4 mb-2 text-md-right">
                            <b-input-group prepend="Show" append="/ Page">
                                <b-form-select :options="pageOptions" v-model="perPage" />
                            </b-input-group>
                        </b-col>
                    </b-row>

                    <b-table ref="discrepancyReport" show-empty striped hover :items="items" :fields="fields"
                        :current-page="currentPage" :per-page="perPage" :filter="filter" :sort-by.sync="sortBy"
                        :sort-desc.sync="sortDesc" :sort-direction="sortDirection" responsive>
                        <template v-slot:cell(dispatchNo)="row">
                            <span class="numFont">
                                {{ row.item.dispatchNo }}
                            </span>
                        </template>
                        <template v-slot:cell(dispatched)="row">
                            <span class="numFont">
                                {{ row.item["Dispatched"] }}
                            </span>
                        </template>
                        <template v-slot:cell(received)="row">
                            <span class="numFont">
                                {{ row.item["Received"] }}
                            </span>
                        </template>
                        <template v-slot:cell(discrepancies)="row">
                            <span class="numFont">
                                {{ row.item["Discrepancies"] }}
                            </span>
                        </template>
                        <template v-slot:cell(status)="row">
                            <DiscrepancyReportRowStatus :row="row" />
                        </template>
                        <template v-slot:cell(actions)="row">
                            <DiscrepancyReportRowActions :row="row" :isSuperAdmin="isSuperAdmin" :isManager="isManager"
                                :isSupervisor="isSupervisor" />
                        </template>

                        <template slot="row-details" slot-scope="row">
                            <DiscrepancyReportDetailsView :row="row" />
                        </template>
                    </b-table>

                    <b-row>
                        <b-col sm="10" class="my-1">
                            <span class="total-display">Total: {{ items.length }}</span>
                        </b-col>
                        <b-col sm="2">
                            <b-pagination align="right" :total-rows="items.length" :per-page="perPage"
                                v-model="currentPage" class="my-0" />
                        </b-col>
                    </b-row>
                </div>
                <div v-else>
                    <b-card> No Results Found. </b-card>
                </div>

            </div>
        </b-card>

        <EditDiscrepancyReport />
        <MultipleEditDiscrepancyReport />
    </div>
</template>

<script>
// Components
import DiscrepancyReportRowStatus from '@/views/reports/discrepancyReport/DiscrepancyReportRowStatus.vue';
import DiscrepancyReportRowActions from '@/views/reports/discrepancyReport/DiscrepancyReportRowActions.vue';
import DiscrepancyReportDetailsView from '@/views/reports/discrepancyReport/DiscrepancyReportDetailsView.vue';
import EditDiscrepancyReport from '@/views/reports/discrepancyReport/EditDiscrepancyReport.vue';
import MultipleEditDiscrepancyReport from '@/views/reports/discrepancyReport/MultipleEditDiscrepancyReport.vue';

// Utils
import { DateUtil } from '@/utils/dateutil';
import { DispatchUtil } from '@/utils/dispatchUtil';
import { DropDownItemsUtil } from '@/utils/dropDownItemsUtil';

// API & DAO
import dispatchApi from '@/api/dispatchApi';
import dispatchDAO from '@/database/dispatches';
import assetPoolDistributionDAO from '@/database/assetPoolDistributions';

// Others
import JsonExcel from 'vue-json-excel';
import EventBus from "@/shared/event-bus";
import config from '@/config/env-constants';
import Loading from 'vue-loading-overlay';
import moment from 'moment';
import 'vue-loading-overlay/dist/vue-loading.css';
import _ from 'lodash';
import { Validator } from 'vee-validate';

export default {
    name: 'discrepancy-report',
    components: {
        JsonExcel,
        Loading,
        DiscrepancyReportRowStatus,
        DiscrepancyReportRowActions,
        DiscrepancyReportDetailsView,
        EditDiscrepancyReport,
        MultipleEditDiscrepancyReport,
    },
    data() {
        return {
            items: [],
            fields: [
                {
                    key: 'dispatchNo',
                    label: 'Dispatch No.',
                    sortable: true,
                },
                {
                    key: 'Date Deployed',
                    sortable: true,
                },
                {
                    key: 'Date Received',
                    sortable: true,
                },
                {
                    key: 'Source',
                    sortable: true,
                },
                {
                    key: 'Destination',
                    sortable: true,
                },
                {
                    key: 'Asset Type',
                    sortable: true,
                },
                {
                    key: 'Trucker',
                },
                {
                    key: 'Dispatched',
                    label: 'Dispatched',
                    sortable: true,
                },
                {
                    key: 'Received',
                    label: 'Received',
                    sortable: true,
                },
                {
                    key: 'Discrepancies',
                    label: 'Discrepancies',
                    sortable: true,
                },
                {
                    key: 'status',
                    class: 'text-center',
                },
                {
                    key: 'actions',
                    thClass: 'text-center',
                },
            ],

            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            pageOptions: [5, 10, 15, 25, 50, 100],
            sortBy: null,
            sortDesc: false,
            sortDirection: 'asc',
            filter: null,

            dateFormatOptions: { ...config.dateFormatOptions },

            totalExpectedAssets: 0,
            totalActualdAssets: 0,
            totalDiscrepancies: 0,

            defaultFilterBy: {
                dateFrom: moment().format('YYYY-MM-DD'),
                dateTo: moment().format('YYYY-MM-DD'),
                sourceCompany: { ...config.companyDefaultValue },
                destinationCompany: { ...config.companyDefaultValue },
                status: 'Pending',
                dispatchNo: '',
            },

            filterBy: {
                dateFrom: moment().format('YYYY-MM-DD'),
                dateTo: moment().format('YYYY-MM-DD'),
                sourceCompany: { ...config.companyDefaultValue },
                destinationCompany: { ...config.companyDefaultValue },
                status: 'Pending',
                dispatchNo: '',
            },

            prevFilterBy: {},
            params: {},

            allCompaniesObj: {},
            allConnectedCompaniesObj: {},
            allConnectionsObj: {},
            allDispatchesObj: {},
            allAssetPoolDistributionsObj: {},

            allSourceCompanyOptions: [],
            allDestinationCompanyOptions: [],
            statusOptions: config.discrepancyReportStatus,

            isSuperAdmin: this.$store.getters.isSuperAdmin,
            isManager: this.$store.getters.isManager,
            isSupervisor: this.$store.getters.isSupervisor,
            loggedUser: this.$store.getters.loggedUser,
            loggedUserCompany: this.$store.getters.loggedUserCompany,

            isLoading: false,

            // Listener
            dispatchListener: null,
            assetPoolDistributionListener: null,
        };
    },
    watch: {
        "filterBy.sourceCompany": function (newVal) {
            // Destination companies includes connected
            const destinationAllCompaniesObj = { ...this.allCompaniesObj, ...this.allConnectedCompaniesObj };

            if (!this.isSuperAdmin && newVal && newVal.id !== null) {
                let companyId = newVal.id;
                let parentCompanyId = newVal.parentCompanyId;

                if (companyId !== this.loggedUserCompany.id && parentCompanyId !== this.loggedUserCompany.id) {
                    let companiesObj = _.filter(destinationAllCompaniesObj, o => {
                        return o.id === this.loggedUserCompany.id || o.parentCompanyId === this.loggedUserCompany.id;
                    });
                    this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(companiesObj);
                } else {
					this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(destinationAllCompaniesObj);
                }
            } else {
                this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(destinationAllCompaniesObj);
            }

            // Reset selected if not on new set of dropdown
            if (this.filterBy.destinationCompany) {
                if (!_.some(this.allDestinationCompanyOptions, { value: { id: this.filterBy.destinationCompany.id } })) {
                    this.filterBy.destinationCompany = { ...config.companyDefaultValue }
                }
            }
        },
        "filterBy.destinationCompany": function (newVal) {
            if (!this.isSuperAdmin && newVal && newVal.id !== null) {
                let companyId = newVal.id;
                let parentCompanyId = newVal.parentCompanyId;

                if (companyId !== this.loggedUserCompany.id && parentCompanyId !== this.loggedUserCompany.id) {
                    let companiesObj = _.filter(this.allCompaniesObj, o => {
                        return o.id === this.loggedUserCompany.id || o.parentCompanyId === this.loggedUserCompany.id;
                    });
                    this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(companiesObj);
                } else {
                    this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
                }
            } else {
                this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
            }

            // Reset selected if not on new set of dropdown
            if (this.filterBy.sourceCompany) {
                if (!_.some(this.allSourceCompanyOptions, { value: { id: this.filterBy.sourceCompany.id || 0 } })) {
                    this.filterBy.sourceCompany = { ...config.companyDefaultValue }
                }
            }
        },
    },
    computed: {
        dateFrom() {
            const dateTo = moment();
            const dateFrom = dateTo.add(-30, 'days');
            return dateFrom.format('YYYY-MM-DD');
        },
        dateTo() {
            return moment().format('YYYY-MM-DD');
        },
        sourceIsRequired() {
            return !this.isSuperAdmin
        },

        exportData() {
            let exportData = [...this.items];

            let totalExpectedAssets = 0;
            let totalActualAssets = 0;
            let totalDiscrepancies = 0;

            _.forEach(exportData, data => {
                totalExpectedAssets += data['Dispatched'];
                totalActualAssets += data['Received']
                totalDiscrepancies += data['Discrepancies'];
            });

            if (exportData.length > 0) {
                exportData.push({
                    'Trucker': 'TOTAL',
                    'Dispatched': totalExpectedAssets,
                    'Received': totalActualAssets,
                    'Discrepancies': totalDiscrepancies
                });
            }

            return exportData;
        },

        exportFields() {
            return {
                'Dispatch No.': 'dispatchNo',
                'Date Deployed': 'Date Deployed',
                'Date Received': 'Date Received',
                Source: 'Source',
                Destination: 'Destination',
                'Asset Type': 'Asset Type',
                Trucker: 'Trucker',
                'Dispatched': 'Dispatched',
                'Received': 'Received',
                'Discrepancies': 'Discrepancies',
                'Status': 'Status',
                'Remarks': 'remarks'
            }
        },

        fileName() {
            let currTimeStamp = DateUtil.getCurrentTimestamp();
            return 'Discrepancy-Report-' + DateUtil.getDateInDDMMYYYYHHSSFormat(currTimeStamp);
        },

        canBulkEdit() {
            return this.isSuperAdmin || this.isManager || this.isSupervisor;
        }
    },
    created() {
        // Set Default Source Company
        if (!this.isSuperAdmin) {
            this.defaultFilterBy.sourceCompany = DropDownItemsUtil.getCompanyItem(this.loggedUserCompany);
        }

        // Set Default Date Range
        this.defaultFilterBy.dateFrom = this.dateFrom;
        this.defaultFilterBy.dateTo = this.dateTo;

        this.$watch(
            () => this.$route,
            (newRoute) => {
                const newId = newRoute.params.id;

                // reset to default
                this.filterBy = {
                    ...this.defaultFilterBy,
                };
                this.filterBy.dispatchNo = newId;

                const dateDeployed = Number(this.$route.query.dateDeployed);
                const status = this.$route.query.status;

                if (!isNaN(dateDeployed)) {
                    const originalDate = new Date(dateDeployed);
                    originalDate.setDate(originalDate.getDate() + 1);

                    this.filterBy.dateFrom = moment(dateDeployed).format('YYYY-MM-DD');
                    this.filterBy.dateTo = moment(originalDate).format('YYYY-MM-DD');
                }

                if (status) {
                    this.filterBy.status = status;
                }

                this.onFilterRequest();
            }
        );
    },
    mounted() {
        setTimeout(async () => {
            try {
                // Show loading indicator
                this.isLoading = true;

                this.allCompaniesObj = { ...this.$store.getters.companies };
                this.allConnectedCompaniesObj = { ...this.$store.getters.connectedCompanies };
                this.allConnectionsObj = { ...this.$store.getters.connections };

				// Create Source and Destination Company Options
				if (this.isSuperAdmin) {
					this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
					this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
				} else {
					this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
					this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allConnectedCompaniesObj);
				}

                await this.resetFilters(true);
            } catch (error) {
                this.$toaster.error('Error loading data. Please reload the page again.');
            } finally {
                // hide loading indicator
                this.isLoading = false;
            }

        }, config.timeout);

        // Event Listeners
        EventBus.$on("onCloseEditDiscrepancyReport", (discrepancyReportObj) => {
            this.updateTable(discrepancyReportObj);
        });

        Validator.extend('discrepancyRange', {
            getMessage: (field, [min, max]) => `${field} must be a whole number between ${min} and ${max}.`,
            validate: (value, [min, max]) => {
                const val = Number(value);
                return value !== '' && Number.isInteger(val) && val >= Number(min) && val <= Number(max);
            }
        });
    },
    methods: {
        async dispatchListenerCallback(type, dispatch) {
            if ((!this.allDispatchesObj[dispatch.id] && type === "added") || type === "modified") {
                this.allDispatchesObj[dispatch.id] = dispatch;

                this.processDispatches(this.allDispatchesObj);
            }
        },
        async assetPoolDistributionListenerCallback(type, assetPoolDistribution) {
            if ((!this.allAssetPoolDistributionsObj[assetPoolDistribution.id] && type === "added") || type === "modified") {
                this.allAssetPoolDistributionsObj[assetPoolDistribution.id] = assetPoolDistribution;

                this.processDispatches(this.allDispatchesObj);
            }
        },
        getValidationParam(isRequired, regex) {
            return {
                required: isRequired,
                regex: regex,
            };
        },
        dateFromDisabled(_ymd, date) {
            return date > new Date();
        },
        validateFilter() {
            let isValid = true;

            if (_.isEmpty(this.filterBy.dateFrom) && _.isEmpty(this.filterBy.dateTo)) {
                this.$toaster.warning('Date From and Date To are required.');
                isValid = false;
            } else if ((_.isEmpty(this.filterBy.dateFrom) && !_.isEmpty(this.filterBy.dateTo)) ||
                (!_.isEmpty(this.filterBy.dateFrom) && _.isEmpty(this.filterBy.dateTo))) {
                this.$toaster.warning('Invalid Date Range. Date From and Date To must both have value.');
                isValid = false;
            } else if (this.filterBy.dateFrom > this.filterBy.dateTo) {
                this.$toaster.warning('Invalid Date Range. Date From must be less than Date To.');
                isValid = false;
            } else if (DateUtil.getNoOfDays(this.filterBy.dateFrom, this.filterBy.dateTo) > 90) {
                this.$toaster.warning('Invalid Date Range. Data range is allowed up to 90 days difference.');
                isValid = false;
            }

            return isValid;
        },
        updateTable(discrepancyReportObj) {
            if (_.isEmpty(discrepancyReportObj)) {
                return;
            }

            _.forEach(discrepancyReportObj, (item) => {
                if (_.has(this.allDispatchesObj, item.id)) {
                    // Merge the update into the existing object
                    this.allDispatchesObj[item.id] = _.assign({}, this.allDispatchesObj[item.id], item);
                }

                if (_.has(this.allAssetPoolDistributionsObj, item.assetPoolDistribution.id)) {
                    this.allAssetPoolDistributionsObj[item.assetPoolDistribution.id] =
                        _.assign({}, this.allAssetPoolDistributionsObj[item.assetPoolDistribution.id], item.assetPoolDistribution);
                }
            });

            this.processDispatches(this.allDispatchesObj);
        },
        async onFilterRequest() {
            let isValid = await this.$validator.validateAll();
            if (!isValid) {
                this.$toaster.warning('Please address the field/s with invalid input.');
                // hide loading indicator
                this.isLoading = false;
                return;
            }

            if (!this.validateFilter()) {
                return;
            }

            if (!_.isEqual(this.filterBy, this.prevFilter)) {
                await this.retrieveData();
                this.prevFilter = { ...this.filterBy };
            }
        },
        async resetFilters(fromMount) {
            if (!_.isEqual(this.filterBy, this.defaultFilterBy)) {
                // reset to default
                this.filterBy = { ...this.defaultFilterBy };
                this.prevFilter = { ...this.filterBy };

                if (fromMount && this.$route.params.id) {
                    let newId = this.$route.params.id;
                    this.filterBy.dispatchNo = newId;

                    const dateDeployed = Number(this.$route.query.dateDeployed);
                    const status = this.$route.query.status;

                    if (!isNaN(dateDeployed)) {
                        const originalDate = new Date(dateDeployed);
                        originalDate.setDate(originalDate.getDate() + 1);

                        this.filterBy.dateFrom = moment(dateDeployed).format('YYYY-MM-DD');
                        this.filterBy.dateTo = moment(originalDate).format('YYYY-MM-DD');
                    }

                    if (status) {
                        this.filterBy.status = status;
                    }
                }

                // reset validation
                this.$validator.reset();
                this.errors.clear();

                await this.retrieveData();
            }
        },
        async onClickReset() {
            if (this.$route.params.id) {
                let pathSegments = this.$route.path.split("/");
                pathSegments.pop(); // Remove the last segment (id)
                let newPath = pathSegments.join("/");

                this.$router.replace({ path: newPath });
            }

            // Reset filters
            await this.resetFilters(false);
        },
        async retrieveData() {
            try {
                // show loading indicator
                this.isLoading = true;

                let filter = { ...this.filterBy };
                filter.fromTimestamp = DateUtil.startDateTimeStamp(new Date(filter.dateFrom));
                filter.toTimestamp = DateUtil.endDateTimeStamp(new Date(filter.dateTo));

                let view = this.isSuperAdmin ? config.view.ADMIN : config.view.COMPANY;

                filter.companyId = this.loggedUserCompany.id;

                // Get all received dispatches and filter client-side
                filter.status = 'Received';
                const { data } = await dispatchApi.getDispatches(
                    filter,
                    view,
                    this.loggedUser.id
                );

                this.allDispatchesObj = data.dispatches;
                this.allAssetPoolDistributionsObj = data.assetPoolDistributions;

                if (_.isEmpty(this.allDispatchesObj)) {
                    this.$toaster.warning('No Results found.');
                } else {
                    this.processDispatches(this.allDispatchesObj);
                }

                this.dispatchListener = dispatchDAO.getDispatchListener(filter, this.dispatchListenerCallback);
                this.assetPoolDistributionListener = assetPoolDistributionDAO.getAssetPoolDistributionListener(filter, this.assetPoolDistributionListenerCallback);
            } catch (error) {
                this.$toaster.error('Error loading data. Please reload the page again.');
            } finally {
                // hide loading indicator
                this.isLoading = false;
            }
        },

        processDispatches(dispatches) {
            this.items = [];

            const assetPoolDistributions = Object.values(this.allAssetPoolDistributionsObj);
            const filterByStatus = this.filterBy.status;

            _.forEach(dispatches, dispatch => {
                for (const asset of dispatch.assets) {
                    if (asset) {
                        // Include ONLY "Lost" discrepancy for this report
                        const discrepancies = asset.expectedQuantity - asset.actualQuantity;
                        const computedDiscrepancies = discrepancies - (asset.discrepancies || 0);
                        const status = asset.status || 'Pending';

                        if (discrepancies > 0 && (filterByStatus === null || filterByStatus === status)) {
                            let item = {};

                            // Parse source and destination display
                            item.id = dispatch.id;
                            item.dispatchNo = dispatch.dispatchNo;
                            item.dateDeployed = dispatch.dateDeployed;
                            item.assets = dispatch.assets;
                            item.assetTypeId = asset.assetTypeId;
                            item.remarks = asset.remarks;

                            const currentAPD = _.find(assetPoolDistributions, x =>
                                x.connectedCompanyId === dispatch.source.companyId &&
                                x.connectedStorageLocationId === dispatch.source.storageLocationId &&
                                x.assetTypeId === asset.assetTypeId &&
                                x.type === 'On-Hand'
                            );

                            item.assetPoolDistribution = currentAPD;
                            item.currentTotal = currentAPD ? currentAPD.total : 0;
                            item['Source'] = DispatchUtil.getCompanyLocationDisplay(dispatch.source);
                            item['Destination'] = DispatchUtil.getCompanyLocationDisplay(dispatch.destination);
                            item['Asset Type'] = asset.assetType;
                            item['Trucker'] = dispatch.transportation.plateNo + ' - ' + dispatch.driver.name;

                            let expectedAssets = asset.expectedQuantity;
                            let actualAssets = asset.actualQuantity;

                            // Update item properties
                            item['Dispatched'] = expectedAssets;
                            item['Received'] = actualAssets;
                            item['Discrepancies'] = computedDiscrepancies;

                            // Parse timestamps
                            item['Date Deployed'] = this.getDisplayDateTime(dispatch.dateDeployed);
                            item['Date Received'] = this.getDisplayDateTime(dispatch.dateReceived);

                            item['Status'] = asset.status || 'Pending';

                            this.items.push(item);
                        }
                    }
                }
            });

            this.items = _.sortBy(this.items, ['dateCreated']);
            this.items.reverse();
            this.totalRows = this.items.length;
        },
        bulkEditDiscrepancyReports() {
            this.$bvModal.show("multiple-edit-discrepancy-report");
        },

        // UTILS
        getDisplayDateTime(date) {
            return DateUtil.getFormattedDateWithTime(date);
        },
    }
};
</script>